import {
  type PositionStatus as ApiPositionStatus,
  type MarketID,
  PositionStatusClosed,
  PositionStatusDeleveraged,
  PositionStatusFailed,
  PositionStatusLiquidated,
  PositionStatusOpen,
  type PositionStatusReason,
  PositionStatusUnspecified,
  PositionStatusUnwound,
} from '@/api/spec';
import type { MarketBoost } from '@/common/boostedMarkets';
import type { Side } from '@/common/constants';

export enum PositionStatus {
  open = 'open',
  closed = 'closed',
  liquidated = 'liquidated',
  deleveraged = 'deleveraged',
  unwound = 'unwound',
  failed = 'failed',
}

export type PositionCore = {
  positionId: number; // api id
  uniqueId: string; // historical event id
  txHash?: string;
  marketId: MarketID;
  side: Side;
  assetCurrency: string;
  assetDecimals: number;
  quoteDecimals: number;
  initialLeverage: number;
  size: number;
  entryPrice: number;
};

export type ViewPosition = PositionCore & {
  leverage: number;
  openTimestamp?: number;
  isBuy?: boolean;
  status: PositionStatus;
  statusReason?: PositionStatusReason;
  value: number;
  markPrice: number;
  pnl: number;
  realizedPnl?: number;
  fee?: number;
  roe: number;
  liquidationPrice: number;
  margin: number;
  marginType: 'cross' | 'isolated';
  funding: number;
  takeProfit?: number;
  stopLoss?: number;
  takeProfitLimitPrice?: number;
  stopLossLimitPrice?: number;
  tpSlPercentage?: number;
  boost?: MarketBoost; // Boost active on the position's market, shown as the boost icon + tooltip
};

const apiToViewStatus: Partial<Record<ApiPositionStatus, PositionStatus>> = {
  [PositionStatusUnspecified]: PositionStatus.open,
  [PositionStatusOpen]: PositionStatus.open,
  [PositionStatusClosed]: PositionStatus.closed,
  [PositionStatusLiquidated]: PositionStatus.liquidated,
  [PositionStatusDeleveraged]: PositionStatus.deleveraged,
  [PositionStatusUnwound]: PositionStatus.unwound,
  [PositionStatusFailed]: PositionStatus.failed,
};

export const mapApiPositionStatus = (status: ApiPositionStatus): PositionStatus => {
  const mapped = apiToViewStatus[status];
  if (!mapped) {
    console.warn(`Unknown position status received: ${status}`);
    return PositionStatus.open;
  }
  return mapped;
};

export const terminalPositionStatuses: ReadonlySet<PositionStatus> = new Set([
  PositionStatus.closed,
  PositionStatus.liquidated,
  PositionStatus.deleveraged,
  PositionStatus.unwound,
  PositionStatus.failed,
]);
